import React, { useState, useEffect } from 'react';
import { 
  testNotificationsInDevelopment, 
  resetSubscription,
  getIOSVersion,
  isIOS,
  getDeviceInfo,
  sendTestNotification 
} from '../utils/notificationUtils';

/**
 * DevNotificationTester - Component for testing and troubleshooting notifications
 * Now with enhanced iOS support and force registration capabilities
 */
const DevNotificationTester = () => {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [isIosDevice, setIsIosDevice] = useState(null);
  const [iosVersion, setIosVersion] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [forceResetting, setForceResetting] = useState(false);
  const [forceResetResult, setForceResetResult] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);

  // Detect iOS on component mount
  // Function to fetch and display active push subscription data
  const fetchSubscriptionData = async () => {
    try {
      if (!('serviceWorker' in navigator)) {
        setSubscriptionData({ error: 'Service Worker not supported' });
        return;
      }
      
      const swRegistration = await navigator.serviceWorker.ready;
      const subscription = await swRegistration.pushManager.getSubscription();
      
      if (!subscription) {
        setSubscriptionData({ 
          status: 'No active subscription found',
          hasCriticalFields: false,
          timestamp: new Date().toLocaleTimeString()
        });
        return;
      }
      
      // Extract the key data that should appear in the database
      let p256dhKey = null;
      let authKey = null;
      
      try {
        if (subscription.getKey) {
          const p256dh = subscription.getKey('p256dh');
          if (p256dh) {
            p256dhKey = btoa(String.fromCharCode.apply(null, new Uint8Array(p256dh)));
          }
          
          const auth = subscription.getKey('auth');
          if (auth) {
            authKey = btoa(String.fromCharCode.apply(null, new Uint8Array(auth)));
          }
        }
      } catch (keyError) {
        console.error('Error extracting keys:', keyError);
      }
      
      // Check if we have the critical fields
      const hasCriticalFields = Boolean(
        subscription.endpoint && p256dhKey && authKey
      );
      
      // Format subscription data for display
      setSubscriptionData({
        endpoint: subscription.endpoint || 'Missing',
        p256dh: p256dhKey ? `${p256dhKey.substring(0, 10)}...` : 'Missing',
        auth: authKey ? `${authKey.substring(0, 5)}...` : 'Missing',
        hasCriticalFields,
        status: hasCriticalFields ? 'Valid subscription' : 'Incomplete subscription',
        timestamp: new Date().toLocaleTimeString()
      });
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setSubscriptionData({ error: error.message });
    }
  };

  useEffect(() => {
    const detectIOS = async () => {
      const isIosDetected = isIOS();
      setIsIosDevice(isIosDetected);
      
      if (isIosDetected) {
        const version = getIOSVersion();
        setIosVersion(version);
      }
      
      const info = getDeviceInfo();
      setDeviceInfo(info);
      
      // Also fetch subscription data on mount
      await fetchSubscriptionData();
    };
    
    detectIOS();
  }, []);
  
  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    
    try {
      const testResult = await testNotificationsInDevelopment();
      setResult(testResult);
      
      // Refresh subscription data after testing
      await fetchSubscriptionData();
    } catch (error) {
      setResult({ error: error.message });
    } finally {
      setTesting(false);
    }
  };
  
  const handleReset = async () => {
    setIsResetting(true);
    setResetResult(null);
    
    try {
      const result = await resetSubscription();
      setResetResult(result);
      
      // Update device info after reset
      const info = getDeviceInfo();
      setDeviceInfo(info);
      
      // Refresh subscription data
      await fetchSubscriptionData();
      
      // Auto-run test after reset
      setTimeout(handleTest, 1000);
    } catch (error) {
      setResetResult({ 
        success: false, 
        error: error.message 
      });
    } finally {
      setIsResetting(false);
    }
  };
  
  // Handle Force iOS Reset - Special function for problematic iPhone detection
  const handleForceIOSReset = async () => {
    setForceResetting(true);
    setForceResetResult(null);
    
    try {
      const result = await resetSubscription(true); // Pass true to force iOS detection
      setForceResetResult(result);
      
      // Update device info after reset
      const info = getDeviceInfo(true); // Get device info with forced iOS
      setDeviceInfo(info);
      
      // Refresh subscription data after forcing iOS
      await fetchSubscriptionData();
      
      // Auto-run test after reset
      setTimeout(handleTest, 1000);
    } catch (error) {
      setForceResetResult({ 
        success: false, 
        error: error.message 
      });
    } finally {
      setForceResetting(false);
    }
  };
  
  // Subscription data display component
  const SubscriptionDataDisplay = () => {
    if (!subscriptionData) return null;
    
    // Determine status colors based on critical fields
    const statusColor = subscriptionData.hasCriticalFields ? '#28a745' : '#dc3545';
    const statusBackgroundColor = subscriptionData.hasCriticalFields ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)';
    
    const getFieldStatus = (field, value) => {
      const hasValue = value && value !== 'Missing';
      const style = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '5px',
        padding: '5px',
        backgroundColor: hasValue ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
        borderRadius: '5px'
      };
      
      return (
        <div style={style}>
          <span style={{ marginRight: '8px' }}>{hasValue ? '✅' : '❌'}</span>
          <strong>{field}:</strong>
          <span style={{ marginLeft: '5px', wordBreak: 'break-all', flex: 1 }}>
            {value || 'Missing'}
          </span>
        </div>
      );
    };
    
    return (
      <div style={{ 
        border: `2px solid ${statusColor}`, 
        borderRadius: '10px', 
        padding: '15px',
        marginTop: '20px',
        backgroundColor: statusBackgroundColor
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: statusColor, margin: 0 }}>
            📊 Push Subscription Status
          </h3>
          <button 
            onClick={() => setShowSubscriptionDetails(!showSubscriptionDetails)}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${statusColor}`,
              color: statusColor,
              padding: '5px 10px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            {showSubscriptionDetails ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        
        <div style={{ marginTop: '15px' }}>
          <p style={{ fontWeight: 'bold', fontSize: '16px' }}>
            {subscriptionData.status || 'Status Unknown'}
          </p>
          
          {subscriptionData.error ? (
            <p style={{ color: '#dc3545' }}><strong>Error:</strong> {subscriptionData.error}</p>
          ) : (
            <>
              <p style={{ marginBottom: '10px' }}>
                {subscriptionData.hasCriticalFields 
                  ? '✅ All required fields are present - notifications should work!' 
                  : '❌ Missing critical fields - notifications will fail!'}
              </p>
              
              <p style={{ fontSize: '14px', marginBottom: '15px', fontStyle: 'italic' }}>
                This is exactly why the database records looked different in your screenshot!
              </p>
              
              {showSubscriptionDetails && (
                <div style={{ 
                  backgroundColor: 'white', 
                  padding: '10px', 
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  marginTop: '10px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>Critical Fields Status:</h4>
                  {getFieldStatus('Endpoint URL', subscriptionData.endpoint)}
                  {getFieldStatus('p256dh Key', subscriptionData.p256dh)}
                  {getFieldStatus('auth Key', subscriptionData.auth)}
                  <p style={{ fontSize: '12px', marginTop: '10px', color: '#666' }}>
                    Last updated: {subscriptionData.timestamp}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Special test notification just for iOS
  const handleIOSTest = async () => {
    setTesting(true);
    
    try {
      const sent = await sendTestNotification();
      setResult({
        iosSpecificTest: true,
        notificationSent: sent,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setResult({ 
        iosSpecificTest: true,
        error: error.message 
      });
    } finally {
      setTesting(false);
    }
  };
  
  // iOS permission denied warning
  const IOSPermissionDeniedWarning = () => {
    // Only show this if we're on iOS and permissions are denied
    if (!isIosDevice || deviceInfo?.notificationPermission !== 'denied') return null;
    
    return (
      <div style={{ 
        border: '2px solid #ff3b30', 
        borderRadius: '10px', 
        padding: '15px',
        marginTop: '20px',
        marginBottom: '20px',
        backgroundColor: 'rgba(255, 59, 48, 0.1)'
      }}>
        <h3 style={{ color: '#ff3b30' }}>⚠️ CRITICAL: NOTIFICATIONS BLOCKED</h3>
        <p style={{ fontSize: '16px', fontWeight: 'bold' }}>
          Notifications are currently BLOCKED for this website on your iPhone!
        </p>
        <p>
          This is why push notifications aren't working. Web Push API requires notification 
          permission to be granted in Safari settings.
        </p>
        <div style={{ 
          backgroundColor: '#fff', 
          padding: '15px', 
          borderRadius: '8px', 
          marginTop: '15px',
          border: '1px solid #ccc'
        }}>
          <h4 style={{ marginTop: 0 }}>How to Fix:</h4>
          <ol style={{ paddingLeft: '20px' }}>
            <li>Open <strong>Settings</strong> on your iPhone</li>
            <li>Scroll down and tap <strong>Safari</strong></li>
            <li>Tap <strong>Advanced</strong></li>
            <li>Tap <strong>Website Data</strong></li>
            <li>Find and tap <strong>{window.location.hostname}</strong></li>
            <li>Enable <strong>Notifications</strong></li>
            <li>Return to this app and click "Reset Subscription"</li>
          </ol>
        </div>
        <p style={{ marginTop: '15px', fontStyle: 'italic' }}>
          Until notifications are enabled in Safari settings, the system will fall back to WebSocket 
          notifications which only work when the app is open.
        </p>
      </div>
    );
  };

  // iOS troubleshooting helper component
  const IOSTroubleshooting = () => (
    <div style={{ 
      border: '2px solid #007aff', 
      borderRadius: '10px', 
      padding: '15px',
      marginTop: '20px',
      backgroundColor: 'rgba(0, 122, 255, 0.1)'
    }}>
      <h3 style={{ color: '#007aff' }}>📱 iPhone Notification Troubleshooting</h3>
      
      <div style={{ marginBottom: '15px' }}>
        <p><strong>iOS Version:</strong> {iosVersion?.version || 'Unknown'}</p>
        <p><strong>Supports Web Push API:</strong> {iosVersion?.supportsWebPush ? '✅ Yes' : '❌ No'}</p>
      </div>
      
      <div style={{ marginBottom: '15px' }}>
        <h4>Steps to Enable Notifications:</h4>
        <ol>
          <li>Make sure your iPhone is on iOS 16.4 or newer</li>
          <li>Add this website to your home screen (tap the Share button, then "Add to Home Screen")</li>
          <li>Open the app from your home screen (not from Safari)</li>
          <li>Click the green "Force iPhone Registration" button below</li>
          <li>Check notification settings: Settings → Safari → [this site] → Notifications</li>
        </ol>
      </div>
      
      <button 
        onClick={handleForceIOSReset}
        disabled={forceResetting}
        style={{
          padding: '12px 20px',
          fontSize: '16px',
          fontWeight: 'bold',
          backgroundColor: '#34c759',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: forceResetting ? 'wait' : 'pointer',
          width: '100%',
          marginBottom: '10px'
        }}
      >
        {forceResetting ? 'Forcing iPhone Registration...' : '📱 Force iPhone Registration'}
      </button>
      
      {forceResetResult && (
        <div style={{ 
          backgroundColor: forceResetResult.success ? 'rgba(52, 199, 89, 0.1)' : 'rgba(255, 59, 48, 0.1)',
          border: `1px solid ${forceResetResult.success ? '#34c759' : '#ff3b30'}`,
          borderRadius: '5px',
          padding: '10px',
          marginTop: '10px'
        }}>
          <p><strong>{forceResetResult.success ? '✅ Success!' : '❌ Error:'}</strong> {forceResetResult.message || forceResetResult.error}</p>
          {forceResetResult.success && (
            <p>Your device is now registered as an iPhone. Please test if notifications are working.</p>
          )}
        </div>
      )}
    </div>
  );
  
  // Device info display
  const DeviceInfoDisplay = () => {
    if (!deviceInfo) return null;
    
    return (
      <div style={{ 
        border: '1px solid #ccc', 
        borderRadius: '8px', 
        padding: '15px',
        marginTop: '20px',
        backgroundColor: '#f9f9f9',
        fontSize: '14px'
      }}>
        <h3>Device Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <p><strong>Device Type:</strong> {deviceInfo.isIOS ? 'iOS Device' : 'Non-iOS Device'}</p>
            <p><strong>Platform:</strong> {deviceInfo.platform}</p>
            <p><strong>Browser:</strong> {deviceInfo.userAgent?.split(' ').slice(-3).join(' ')}</p>
            <p><strong>Name:</strong> {deviceInfo.deviceName}</p>
            <p><strong>Notifications:</strong> {deviceInfo.notificationPermission}</p>
          </div>
          <div>
            <p><strong>iOS Detection:</strong> {deviceInfo.isIOS ? '✅ Detected' : '❌ Not Detected'}</p>
            {deviceInfo.isIOS && (
              <>
                <p><strong>iOS Version:</strong> {deviceInfo.iosVersion || 'Unknown'}</p>
                <p><strong>Using Web Push:</strong> {deviceInfo.usingWebPushAPI ? '✅ Yes' : '❌ No'}</p>
                <p><strong>Forced iOS:</strong> {deviceInfo.forcedIOS ? '✅ Yes' : '❌ No'}</p>
              </>
            )}
            <p><strong>Service Worker:</strong> {deviceInfo.hasServiceWorker ? '✅ Yes' : '❌ No'}</p>
            <p><strong>Push Manager:</strong> {deviceInfo.hasPushManager ? '✅ Yes' : '❌ No'}</p>
          </div>
        </div>
      </div>
    );
  };

  // Device not recognized as iPhone section
  const DeviceNotRecognizedAsIPhone = () => {
    // Only show this section if the device should be an iPhone but isn't detected as one
    const shouldBeIPhone = !isIosDevice && (
      (navigator.userAgent.toLowerCase().includes('iphone')) ||
      (navigator.platform === 'iPhone') || 
      (navigator.userAgent.toLowerCase().includes('ipad')) ||
      (navigator.platform === 'iPad')
    );
    
    if (!shouldBeIPhone) return null;
    
    return (
      <div style={{ 
        border: '2px solid #ff3b30', 
        borderRadius: '10px', 
        padding: '15px',
        marginTop: '20px',
        backgroundColor: 'rgba(255, 59, 48, 0.1)'
      }}>
        <h3 style={{ color: '#ff3b30' }}>⚠️ Device Not Recognized as iPhone?</h3>
        <p>Your device appears to be an iPhone/iPad but is not being detected correctly.</p>
        <p>This is likely why notifications aren't working. Use the special override button:</p>
        
        <button 
          onClick={handleForceIOSReset}
          disabled={forceResetting}
          style={{
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: 'bold',
            backgroundColor: '#ff3b30',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: forceResetting ? 'wait' : 'pointer',
            width: '100%',
            marginTop: '10px'
          }}
        >
          {forceResetting ? 'Overriding Detection...' : '⚠️ Force Register as iPhone'}
        </button>
      </div>
    );
  };

  // EMERGENCY OVERRIDE BANNER - Specifically for devices identified as Win32 Browser in logs
  const EmergencyOverrideBanner = () => {
    // Check if device name includes "Win32 Browser" which matches the issue in server logs
    const isWin32InDeviceName = deviceInfo?.deviceName?.includes("Win32 Browser");
    
    if (!isWin32InDeviceName) return null;
    
    return (
      <div style={{
        backgroundColor: '#ff3b30',
        color: 'white',
        padding: '20px',
        borderRadius: '10px',
        marginBottom: '20px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
      }}>
        <h2 style={{ margin: '0 0 15px 0' }}>⚠️ CRITICAL: DEVICE MISIDENTIFIED</h2>
        <p style={{ fontSize: '16px', marginBottom: '15px' }}>
          <strong>Your iPhone is being identified as "Win32 Browser" in the logs!</strong> This is exactly why 
          notifications aren't working. The browser is providing incorrect device information.
        </p>
        <p style={{ fontSize: '16px', marginBottom: '15px' }}>
          Press this button to override the automatic detection:
        </p>
        <button 
          onClick={handleForceIOSReset}
          disabled={forceResetting}
          style={{
            padding: '15px 20px',
            fontSize: '18px',
            fontWeight: 'bold',
            backgroundColor: 'white',
            color: '#ff3b30',
            border: 'none',
            borderRadius: '8px',
            cursor: forceResetting ? 'wait' : 'pointer',
            width: '100%'
          }}
        >
          {forceResetting ? 'FIXING DEVICE IDENTIFICATION...' : '🔴 EMERGENCY FIX: FORCE IPHONE IDENTIFICATION'}
        </button>
      </div>
    );
  };

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <h2>Notification Tester (Development Mode)</h2>
      
      {/* Emergency override banner */}
      <EmergencyOverrideBanner />
      
      {/* iOS Permission Denied Warning */}
      <IOSPermissionDeniedWarning />
      
      {/* Raw user agent info for debugging */}
      <div style={{
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '20px',
        backgroundColor: '#f8f9fa',
        fontSize: '14px'
      }}>
        <h3 style={{ marginTop: 0 }}>🔍 Device Identification Debug</h3>
        <div style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '12px' }}>
          <strong>Raw User Agent:</strong> {navigator.userAgent}
        </div>
        <div style={{ marginTop: '10px' }}>
          <strong>Detected as iOS:</strong> {isIosDevice ? '✅ Yes' : '❌ No'}
        </div>
        <div style={{ marginTop: '10px' }}>
          <strong>Device Name:</strong> {deviceInfo?.deviceName || 'Unknown'}
        </div>
      </div>
      
      {/* Special section for iOS devices */}
      {isIosDevice && <IOSTroubleshooting />}
      
      {/* Device not recognized as iPhone but should be */}
      <DeviceNotRecognizedAsIPhone />
      
      <div style={{ marginTop: '20px' }}>
        <button 
          onClick={handleTest}
          disabled={testing}
          style={{
            padding: '10px 15px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: testing ? 'wait' : 'pointer',
            marginRight: '10px'
          }}
        >
          {testing ? 'Testing...' : 'Test Notifications'}
        </button>
        
        <button 
          onClick={handleReset}
          disabled={isResetting}
          style={{
            padding: '10px 15px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: isResetting ? 'wait' : 'pointer'
          }}
        >
          {isResetting ? 'Resetting...' : 'Reset Subscription'}
        </button>
        
        {isIosDevice && (
          <button 
            onClick={handleIOSTest}
            disabled={testing}
            style={{
              padding: '10px 15px',
              marginLeft: '10px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: testing ? 'wait' : 'pointer'
            }}
          >
            iOS-specific Test
          </button>
        )}
      </div>
      
      {result && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '5px',
          backgroundColor: '#f8f9fa'
        }}>
          <h3>Test Results</h3>
          
          {result.iosSpecificTest ? (
            <div>
              <p><strong>iOS Test Notification:</strong> {result.notificationSent ? '✅ Sent' : '❌ Failed'}</p>
              {result.error && <p><strong>Error:</strong> {result.error}</p>}
            </div>
          ) : (
            <div>
              <p><strong>Environment:</strong> {result.environment}</p>
              <p><strong>Permission Status:</strong> {result.permissionStatus}</p>
              <p><strong>Notification Shown:</strong> {result.notificationShown ? '✅ Yes' : '❌ No'}</p>
              {result.iosInfo && (
                <div>
                  <p><strong>iOS Version:</strong> {result.iosInfo.version}</p>
                  <p><strong>Supports Web Push:</strong> {result.iosInfo.supportsWebPush ? '✅ Yes' : '❌ No'}</p>
                </div>
              )}
              <p><strong>Message:</strong> {result.message}</p>
              {result.error && <p><strong>Error:</strong> {result.error}</p>}
            </div>
          )}
        </div>
      )}
      
      {resetResult && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px',
          border: `1px solid ${resetResult.success ? '#28a745' : '#dc3545'}`,
          borderRadius: '5px',
          backgroundColor: resetResult.success ? '#d4edda' : '#f8d7da'
        }}>
          <h3>Reset Result</h3>
          <p><strong>Status:</strong> {resetResult.success ? '✅ Success' : '❌ Failed'}</p>
          <p><strong>Message:</strong> {resetResult.message || resetResult.error}</p>
        </div>
      )}
      
      {/* Display Push Subscription Status */}
      <SubscriptionDataDisplay />
      
      {/* Display device info */}
      <DeviceInfoDisplay />
      
      <div style={{ 
        marginTop: '20px', 
        padding: '15px',
        border: '1px solid #ffc107',
        borderRadius: '5px',
        backgroundColor: '#fff3cd'
      }}>
        <h3>How Notifications Work</h3>
        <ul>
          <li><strong>Modern Browsers (including iOS 16.4+):</strong> Use standard Web Push API</li>
          <li><strong>Older iOS Versions:</strong> Use WebSocket fallback when app is active</li>
          <li><strong>Permission Required:</strong> Always ensure notifications are allowed in browser settings</li>
        </ul>
        
        <h4>iOS-Specific Tips:</h4>
        <ul>
          <li>Add this app to your home screen for best notification support</li>
          <li>Make sure iOS is updated to 16.4 or higher for Web Push support</li>
          <li>Check Settings → Safari → [this site] → Notifications is enabled</li>
          <li>For persistent issues, use the "Force iPhone Registration" button</li>
        </ul>
      </div>
    </div>
  );
};

export default DevNotificationTester;